import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpCdfMagV2Grid from '../../components/kkp/KkpCdfMagV2Grid';
import KkpVenteMagModal from '../../components/kkp/KkpVenteMagModal';
import KkpBatValideModal from '../../components/kkp/KkpBatValideModal';
import { useKkpBatValideFlow } from '../../hooks/useKkpBatValideFlow';
import kkpService from '../../services/kkpService';
import { loadCdfMagFilters, saveCdfMagFilters } from '../../utils/kkpCdfMagFiltersStorage';
import { canonicalMagPeriod } from '../../utils/kkpMagPeriodUtils';
import { showViewportNotice } from '../../utils/showViewportNotice';
import { AppAlert, AppSpinner } from '../../components/ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/kkp-cdf-mag-v2.scss';

const KkpCdfMagPage = () => {
  const { ask, modal } = useAppConfirm();
  const currentYear = new Date().getFullYear();

  const [meta, setMeta] = useState(null);
  const [defaultCommercialId, setDefaultCommercialId] = useState('');
  const [annee, setAnnee] = useState(null);
  const [mois, setMois] = useState(null);
  const [periodReady, setPeriodReady] = useState(false);
  const [cdf, setCdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showVenteModal, setShowVenteModal] = useState(false);
  const [editingVente, setEditingVente] = useState(null);
  const [modalInitialValues, setModalInitialValues] = useState({});
  const [lockVenteFormat, setLockVenteFormat] = useState(false);

  useEffect(() => {
    kkpService.getMagMeta()
      .then((data) => {
        setMeta(data);
        setDefaultCommercialId(data.current_commercial_id ? String(data.current_commercial_id) : '');
        const period = data.default_period;
        if (period?.annee && period?.mois) {
          const canon = canonicalMagPeriod(Number(period.annee), Number(period.mois));
          setAnnee(canon.annee);
          setMois(canon.mois);
        } else {
          const saved = loadCdfMagFilters();
          if (saved) {
            setAnnee(saved.annee);
            setMois(saved.mois);
          } else {
            const now = new Date();
            const canon = canonicalMagPeriod(now.getFullYear(), now.getMonth() + 1);
            setAnnee(canon.annee);
            setMois(canon.mois);
          }
        }
        setPeriodReady(true);
      })
      .catch((err) => setError(err.message));
  }, []);

  const loadCdf = useCallback(async (silent = false) => {
    if (!periodReady || annee == null || mois == null) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await kkpService.getCdfMag({ annee, mois });
      setCdf(data);
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [annee, mois, periodReady]);

  const {
    batValideVente,
    batValideSubmitting,
    batValideError,
    openBatValide,
    closeBatValide,
    submitBatValide,
  } = useKkpBatValideFlow({
    onUpdated: () => loadCdf(true),
  });

  useEffect(() => {
    loadCdf();
  }, [loadCdf]);

  useEffect(() => {
    if (!periodReady || annee == null || mois == null) return;
    saveCdfMagFilters({ annee, mois });
  }, [annee, mois, periodReady]);

  const goToPeriod = (period) => {
    if (!period?.annee || !period?.mois) return;
    const canon = canonicalMagPeriod(Number(period.annee), Number(period.mois));
    setAnnee(canon.annee);
    setMois(canon.mois);
  };

  const runAction = async (action) => {
    setSaving(true);
    setError(null);
    try {
      const data = await action();
      setCdf(data);
      return data;
    } catch (err) {
      showViewportNotice(err.message, 'warning');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handlePageCountChange = (pageCount) => {
    runAction(() => kkpService.updateCdfMagConfig({ annee, mois, pageCount: Number(pageCount) }));
  };

  const handleSpreadReorder = (spreadId, targetIndex, zone) => {
    runAction(() => kkpService.reorderCdfMagDp({
      annee, mois, spreadId, targetIndex, zone,
    }));
  };

  const handlePageSwap = ({ sourcePageKey, targetPageKey }) => {
    runAction(() => kkpService.swapCdfMagPages({
      annee, mois, sourcePageKey, targetPageKey,
    }));
  };

  const handleOptionalAgendaToggle = async (enabled) => {
    const ok = await ask({
      title: enabled ? 'Restaurer la page Agenda' : 'Retirer la page Agenda',
      message: enabled
        ? 'Restaurer la page Agenda optionnelle ?'
        : 'Retirer la page Agenda optionnelle ? Elle deviendra une page pub libre.',
      confirmLabel: enabled ? 'Restaurer' : 'Retirer',
      confirmVariant: enabled ? 'primary' : 'warning',
    });
    if (!ok) return;
    runAction(() => kkpService.setCdfMagOptionalAgenda({ annee, mois, enabled }));
  };

  const handleMondeFoodLayoutToggle = async (enabled) => {
    const ok = await ask({
      title: enabled ? 'Restaurer Monde Food' : 'Double page normale',
      message: enabled
        ? 'Restaurer la mise en page Monde Food sur cette double page vide ?'
        : 'Transformer cette double page vide en double page normale ?',
      confirmLabel: enabled ? 'Restaurer' : 'Transformer',
      confirmVariant: enabled ? 'primary' : 'warning',
    });
    if (!ok) return;
    runAction(() => kkpService.setCdfMagMondeFoodLayout({ annee, mois, enabled }));
  };

  const handlePlacementMove = ({ venteId, spreadId, pageKey, slotId }) => {
    runAction(() => kkpService.moveCdfMagPlacement({
      annee, mois, venteId, spreadId, pageKey, slotId,
    }));
  };

  const handleArticleSave = (pageKey, text) => {
    runAction(() => kkpService.saveCdfMagArticle({ annee, mois, pageKey, text }));
  };

  const handleValidatedToggle = async (validated) => {
    const data = await runAction(() => kkpService.setCdfMagValidated({ annee, mois, validated }));
    if (validated && data?.next_period) {
      goToPeriod(data.next_period);
    }
  };

  const openCreateVente = (preset = {}) => {
    const { lockFormat = false, ...values } = preset;
    setEditingVente(null);
    setLockVenteFormat(lockFormat);
    setModalInitialValues({ annee, mois, ...values });
    setShowVenteModal(true);
  };

  const handlePlacementClick = async (placement) => {
    if (!placement?.vente_id) return;
    setSaving(true);
    setError(null);
    try {
      setLockVenteFormat(false);
      const vente = await kkpService.getVenteMag(placement.vente_id);
      if (vente) {
        setEditingVente(vente);
        setShowVenteModal(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVenteChanged = async () => {
    setEditingVente(null);
    await loadCdf(true);
  };

  const moisOptions = meta?.mois ?? [];

  const periodeLabel = cdf
    ? `${cdf.mois_label} ${cdf.annee} — ${cdf.page_count} pages, ${cdf.ventes_placed_count ?? 0}/${cdf.ventes_count} vente(s) placée(s)`
    : 'Chemin de fer magazine';

  return (
    <MasterLayout>
      <Breadcrumb
        title="Chemin de fer MAG"
        subtitle={periodeLabel}
        actions={(
          <div className="d-flex flex-wrap gap-2 align-items-center kkp-cdf-v2-toolbar">
            <select
              className="form-select form-select-sm w-auto"
              value={annee}
              onChange={(e) => setAnnee(Number(e.target.value))}
            >
              {[currentYear, currentYear + 1, currentYear - 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              className="form-select form-select-sm w-auto"
              value={mois}
              onChange={(e) => setMois(Number(e.target.value))}
            >
              {moisOptions.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <button
              type="button"
              className={`btn btn-sm kkp-cdf-validated-toggle ${cdf?.validated ? 'kkp-cdf-validated-toggle--on' : ''}`}
              onClick={() => handleValidatedToggle(!cdf?.validated)}
              disabled={saving || loading}
              title={cdf?.validated ? 'Dévalider la mise en forme' : 'Valider la mise en forme'}
              aria-label={cdf?.validated ? 'Dévalider la mise en forme' : 'Valider la mise en forme'}
              aria-pressed={Boolean(cdf?.validated)}
            >
              <Icon
                icon={cdf?.validated ? 'mdi:check-decagram' : 'mdi:check-decagram-outline'}
                className="text-xl"
              />
            </button>
            {cdf && (
              <select
                className="form-select form-select-sm w-auto"
                value={cdf.page_count}
                onChange={(e) => handlePageCountChange(e.target.value)}
                disabled={saving || cdf.validated}
              >
                {(cdf.page_count_options ?? []).map((n) => (
                  <option key={n} value={n}>{n} pages</option>
                ))}
              </select>
            )}
          </div>
        )}
      />

      {error && <AppAlert>{error}</AppAlert>}

      {saving && (
        <div className="position-fixed top-50 start-50 translate-middle" style={{ zIndex: 2000 }}>
          <AppSpinner centered={false} />
        </div>
      )}

      {loading ? (
        <AppSpinner />
      ) : (
        <div className={`kkp-cdf-v2 card ${cdf?.validated ? 'kkp-cdf-v2--validated' : ''}`}>
          <div className="card-body">
            <KkpCdfMagV2Grid
              spreads={cdf?.spreads ?? []}
              unplaced={cdf?.unplaced ?? []}
              options={cdf?.options ?? []}
              readOnly={Boolean(cdf?.validated)}
              onPlacementMove={handlePlacementMove}
              onSpreadReorder={handleSpreadReorder}
              onPageSwap={handlePageSwap}
              onPlacementClick={handlePlacementClick}
              onLibreClick={(payload) => openCreateVente(
                payload?.format
                  ? {
                    format: payload.format,
                    contenu: payload.contenu ?? 'PUBLICITE',
                    lockFormat: Boolean(payload.lockFormat),
                  }
                  : {},
              )}
              onArticleSave={handleArticleSave}
              onOptionalAgendaToggle={handleOptionalAgendaToggle}
              onMondeFoodLayoutToggle={handleMondeFoodLayoutToggle}
            />
          </div>
        </div>
      )}

      <KkpVenteMagModal
        show={showVenteModal}
        onClose={() => {
          setShowVenteModal(false);
          setEditingVente(null);
          setLockVenteFormat(false);
        }}
        onSaved={handleVenteChanged}
        onDeleted={handleVenteChanged}
        onCleared={handleVenteChanged}
        onRequestBatValide={(nextVente) => {
          setShowVenteModal(false);
          setEditingVente(null);
          openBatValide(nextVente);
        }}
        onError={setError}
        meta={meta}
        commerciaux={meta?.commerciaux ?? []}
        defaultCommercialId={defaultCommercialId}
        vente={editingVente}
        initialValues={modalInitialValues}
        lockFormat={lockVenteFormat}
      />

      <KkpBatValideModal
        show={Boolean(batValideVente)}
        vente={batValideVente}
        onClose={closeBatValide}
        onSubmit={submitBatValide}
        submitting={batValideSubmitting}
        error={batValideError}
      />
      {modal}
    </MasterLayout>
  );
};

export default KkpCdfMagPage;
